"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Bookmark, BookmarkCheck } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useClerk, useUser } from "@clerk/nextjs";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { UserListingSavedFrom } from "@/db/definitions";

type Props = {
  onSavedChange?: (saved: boolean) => void;
  listingId: string;
  isSaved: boolean;
  text: {
    ariaLabel: string;
    default?: string | null;
    saved?: string | null;
    toast: {
      added: string;
      removed: string;
      error: string;
    };
  };
  type: UserListingSavedFrom;
};

const ListingSaveButton: React.FC<Props> = ({
  isSaved,
  onSavedChange,
  listingId,
  text,
  type,
}) => {
  const { user } = useUser();
  const clerk = useClerk();
  const [isOn, setIsOn] = useState(isSaved);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const tUi = useTranslations("ui");

  useEffect(() => {
    setIsOn(isSaved);
  }, [isSaved]);

  const onClickHandler = async () => {
    if (!user) {
      clerk.openSignUp();
      return;
    }

    if (pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    const newIsOn = !isOn;

    setIsOn(newIsOn);

    try {
      const response = await fetch("/api/user/listings/toggle", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isSaved: newIsOn, listingId, type }),
        cache: "no-store",
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as {
          code?: string;
        } | null;
        setIsOn(!newIsOn);
        toast.error(
          data?.code === "LISTING_TRACKED"
            ? tUi("savedListingTracked")
            : text.toast.error,
        );
        return;
      }
      toast.success(newIsOn ? text.toast.added : text.toast.removed);
      onSavedChange?.(newIsOn);
    } catch {
      setIsOn(!newIsOn);
      toast.error(text.toast.error);
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };
  return (
    <Button
      aria-label={text.ariaLabel}
      aria-pressed={isOn}
      disabled={pending}
      aria-busy={pending}
      variant={isOn ? "default" : "outline"}
      size="sm"
      onClick={(e) => {
        e.stopPropagation();
        onClickHandler();
      }}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        {isOn ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, filter: "blur(4px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.5, filter: "blur(4px)" }}
            className="flex items-center gap-2 justify-center w-full"
            key={`user-listing-saved-${listingId}`}
          >
            <BookmarkCheck className="w-4 h-4" />
            {text?.saved && <p>{text.saved}</p>}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.7, filter: "blur(4px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.7, filter: "blur(4px)" }}
            className="flex flex-row gap-2 items-center justify-center w-full"
            key={`user-listing-${listingId}`}
          >
            <Bookmark className="w-4 h-4" />
            {text?.default && <p>{text.default}</p>}
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

export default ListingSaveButton;
